import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Anchor,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  Group,
  Loader,
  Paper,
  Pill,
  SegmentedControl,
  Stepper,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
} from "mantine-react-table";
import { IconHistory, IconSearch } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { useDebouncedValue } from "@mantine/hooks";
import {
  BULK_MAIL_SIGNATURE_END,
  BULK_MAIL_SIGNATURE_START,
  getBulkMailSignatureHtml,
  replaceBulkMailSignature,
  type BulkMailSignatureId,
} from "@shared/bulkMailSignatureTemplates";
import { emailCommonFooterRowHtml } from "@shared/emailCommonFooter";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
const PAGE_SIZE = 15;

const PLATFORM_OVERVIEW_CANVA_URL = "https://canva.link/ze86v2ckorvdp2b";
const WHAT_WE_DO_URL = "https://gtsetu.com/what-we-do/";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(value: string): boolean {
  const t = value.trim();
  return t.length > 3 && EMAIL_RE.test(t);
}

type BulkMailDirectoryRow = {
  userId: string;
  companyId: string | null;
  companyName: string;
  userName: string;
  userEmail: string;
  companyEmail: string | null;
  address: string;
  gtid: string;
  headquartersCountry: string;
  companyDescription: string | null;
  companyType: string;
  companyStatus: string | null;
};

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeHtmlAttribute(url: string): string {
  return url.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

function getPublicDiscoverOrigin(): string {
  const raw = import.meta.env.VITE_PUBLIC_PLATFORM_URL?.trim();
  if (raw) {
    return raw.replace(/\/+$/, "");
  }
  return "https://platform.gtsetu.com";
}

function normalizeCardDescription(
  raw: string | null | undefined,
  fallback: string | null | undefined = undefined
): string {
  const primary = (raw ?? "").replace(/\s+/g, " ").trim();
  const secondary = (fallback ?? "").replace(/\s+/g, " ").trim();
  const t =
    primary ||
    secondary ||
    "View their anonymous profile on GTsetu for more detail.";
  return t.length > 400 ? `${t.slice(0, 397)}...` : t;
}

type CardCompany = {
  companyId: string;
  gtid: string;
  country: string;
  description: string;
};

function collectCardCompanies(rows: BulkMailDirectoryRow[]): CardCompany[] {
  const byId = new Map<string, BulkMailDirectoryRow>();
  for (const r of rows) {
    if (!r.companyId) continue;
    if (!byId.has(r.companyId)) byId.set(r.companyId, r);
  }
  return [...byId.values()].map((r) => ({
    companyId: r.companyId as string,
    gtid: r.gtid,
    country: r.headquartersCountry?.trim() || "—",
    description: normalizeCardDescription(r.companyDescription, r.companyType),
  }));
}

function buildCompanyCardsHtml(cards: CardCompany[], discoverBase: string): string {
  if (cards.length === 0) {
    return `<p style="margin:0;color:#666;font-size:14px;">No partner profiles were selected for this message.</p>`;
  }
  return cards
    .map((c, index) => {
      const discoverUrl = `${discoverBase}/discover/${c.companyId}`;
      const href = escapeHtmlAttribute(discoverUrl);
      const paddingBottom = index < cards.length - 1 ? "12px" : "0";
      return `<tr>
                <td style="padding-bottom:${paddingBottom};">
                  <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e6eaf0; border-radius:8px;">
                    <tr><td style="padding:12px;">
                      <table width="100%" cellpadding="0" cellspacing="0">
                        <tr>
                          <td style="vertical-align:middle; padding-right:12px;">
                            <p style="margin:0; font-weight:bold;">${escapeHtml(c.gtid)} | ${escapeHtml(c.country)}</p>
                            <p style="margin:4px 0 0 0; font-size:13px; color:#555;">
                              ${escapeHtml(c.description)}
                            </p>
                          </td>
                          <td valign="middle" align="right" style="vertical-align:middle; text-align:right; width:1%;">
                            <table cellpadding="0" cellspacing="0" align="right">
                              <tr>
                                <td bgcolor="#0a5fa8" style="border-radius:5px;">
                                  <a href="${href}"
                                     style="display:inline-block; padding:9px 14px; color:#fff; text-decoration:none; white-space:nowrap;">
                                    View Profile
                                  </a>
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </table>
                    </td></tr>
                  </table>
                </td>
              </tr>`;
    })
    .join("");
}

function buildDefaultMarketingHtml(rows: BulkMailDirectoryRow[], signatureId: BulkMailSignatureId): string {
  const platformBase = getPublicDiscoverOrigin();
  const exploreHref = escapeHtmlAttribute(`${platformBase}/`);
  const whatWeDoHref = escapeHtmlAttribute(WHAT_WE_DO_URL);
  const canvaHref = escapeHtmlAttribute(PLATFORM_OVERVIEW_CANVA_URL);
  const cards = collectCardCompanies(rows);
  const companyCardsHtml = buildCompanyCardsHtml(cards, platformBase);
  const firstName = "there";

  return `<table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f4f7fb; font-family:Arial, sans-serif; padding:30px 0;">
    <tr>
      <td align="center">

        <table width="720" cellpadding="0" cellspacing="0" style="background:#ffffff; border-radius:10px; overflow:hidden;">

          <tr>
            <td style="background-color:#012d57; padding:20px; text-align:right;">
              <img src="https://gtsetu.com/wp-content/uploads/2026/02/White-Transperent-Logo.png" height="80" alt="GTsetu">
            </td>
          </tr>

          <tr>
            <td style="padding:40px; font-size:14px; color:#333; line-height:1.7;">

              <p>Hello ${escapeHtml(firstName)},</p>

              <p>
                Hope you are doing well. I wanted to follow up on my earlier messages and check if you had a chance to go through the details about GTsetu.
              </p>

              <p>
                As we are onboarding a select set of early partners, I am sharing the platform details with you below, to give you a better understanding of how GTsetu works and how companies are collaborating on it.
              </p>

              <p>
                GTsetu helps manufacturing, industrial, and technology businesses connect, explore partnerships, and collaborate globally in a structured and secure environment.
              </p>

              <table cellpadding="0" cellspacing="0" style="margin:25px 0;">
                <tr>
                  <td bgcolor="#0a5fa8" style="border-radius:6px;">
                    <a href="${whatWeDoHref}"
                       style="display:inline-block; padding:12px 22px; color:#ffffff; text-decoration:none; font-weight:bold;">
                      What We Do
                    </a>
                  </td>
                  <td width="15"></td>
                  <td bgcolor="#012d57" style="border-radius:6px;">
                    <a href="${exploreHref}"
                       style="display:inline-block; padding:12px 22px; color:#ffffff; text-decoration:none; font-weight:bold;">
                      Explore Platform
                    </a>
                  </td>
                </tr>
              </table>

              <p><strong>Platform snapshot:</strong></p>
              <p>Quick visual overview of how the platform works:</p>

              <table cellpadding="0" cellspacing="0" style="margin:15px 0;">
                <tr>
                  <td bgcolor="#e8f1fb" style="border-radius:6px;">
                    <a href="${canvaHref}"
                       style="display:inline-block; padding:12px 20px; color:#012d57; text-decoration:none; font-weight:bold;">
                      View Platform Overview
                    </a>
                  </td>
                </tr>
              </table>

              <p><strong>Potential partner profiles:</strong></p>

              <table width="100%" cellpadding="0" cellspacing="0">
                ${companyCardsHtml}
              </table>

              <p style="margin-top:20px;">
                Once you onboard and complete your profile, we can connect you with relevant companies actively looking for partnerships.
              </p>

              <p style="margin-top:16px;">
                The onboarding is completely free, and the first collaboration cycle is also free. This allows you to explore the platform and evaluate opportunities without any commitment. We do not charge any commission or profit share or have any hidden fees.
              </p>

              <p>Looking forward to working together!</p>

            </td>
          </tr>

          <tr>
            <td style="padding:30px 40px; border-top:1px solid #eee;">
              ${BULK_MAIL_SIGNATURE_START}
              ${getBulkMailSignatureHtml(signatureId)}
              ${BULK_MAIL_SIGNATURE_END}
            </td>
          </tr>

        ${emailCommonFooterRowHtml({ tdPadding: "30px", includeAutomatedNotice: false })}

        </table>

      </td>
    </tr>
  </table>`;
}

function DirectoryTable(props: {
  stepLabel: string;
  summaryText?: string;
  rows: BulkMailDirectoryRow[];
  loading: boolean;
  totalRows: number;
  pagination: MRT_PaginationState;
  onPaginationChange: React.Dispatch<React.SetStateAction<MRT_PaginationState>>;
  search: string;
  onSearchChange: (v: string) => void;
  selected: Map<string, BulkMailDirectoryRow>;
  onToggleRow: (row: BulkMailDirectoryRow) => void;
  onTogglePage: (pageRows: BulkMailDirectoryRow[], selectAll: boolean) => void;
}) {
  const {
    stepLabel,
    summaryText,
    rows,
    loading,
    totalRows,
    pagination,
    onPaginationChange,
    search,
    onSearchChange,
    selected,
    onToggleRow,
    onTogglePage,
  } = props;

  const columns = useMemo<MRT_ColumnDef<BulkMailDirectoryRow>[]>(
    () => [
      {
        id: "select",
        header: "",
        size: 52,
        Cell: ({ row }) => (
          <Checkbox
            aria-label={`Select ${row.original.userEmail}`}
            checked={selected.has(row.original.userId)}
            onChange={() => onToggleRow(row.original)}
          />
        ),
      },
      {
        accessorKey: "companyName",
        header: "Company name",
        size: 180,
      },
      {
        accessorKey: "userName",
        header: "User name",
        size: 140,
      },
      {
        accessorKey: "userEmail",
        header: "User email",
        size: 200,
      },
      {
        accessorKey: "address",
        header: "Address",
        size: 200,
      },
      {
        accessorKey: "gtid",
        header: "GTID",
        size: 120,
      },
      {
        accessorKey: "companyType",
        header: "Company type",
        size: 140,
      },
      {
        id: "companyStatus",
        header: "Company status",
        size: 130,
        Cell: ({ row }) => {
          const s = row.original.companyStatus;
          if (!s) return <Text size="sm">—</Text>;
          return (
            <Badge size="sm" variant="light" color="blue">
              {s.replace(/_/g, " ")}
            </Badge>
          );
        },
      },
    ],
    [selected, onToggleRow]
  );

  return (
    <Flex direction="column" gap="md">
      <Text size="sm" c="dimmed">
        {summaryText ?? `${stepLabel} · ${selected.size} selected (across all pages)`}
      </Text>
      <Group align="flex-end" wrap="wrap" gap="sm">
        <TextInput
          placeholder="Search by email, company email, company name, or GTID"
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => onSearchChange(e.currentTarget.value)}
          maw={420}
          style={{ flex: "1 1 280px" }}
        />
        <Group gap="xs">
          <Button size="compact-sm" variant="light" onClick={() => onTogglePage(rows, true)}>
            Select page
          </Button>
          <Button size="compact-sm" variant="default" onClick={() => onTogglePage(rows, false)}>
            Clear page
          </Button>
        </Group>
      </Group>
      <MantineReactTable
        columns={columns}
        data={rows}
        getRowId={(row) => row.userId}
        state={{
          isLoading: loading,
          pagination,
        }}
        onPaginationChange={onPaginationChange}
        manualPagination
        rowCount={totalRows}
        enableGlobalFilter={false}
        enableColumnFilters={false}
        mantineTableContainerProps={{ style: { minHeight: 360 } }}
      />
      {loading && rows.length === 0 && (
        <Center py="lg">
          <Loader />
        </Center>
      )}
    </Flex>
  );
}

export default function AdminBulkMailPage() {
  const [active, setActive] = useState(0);

  const [rows, setRows] = useState<BulkMailDirectoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);

  const [contextSelected, setContextSelected] = useState<Map<string, BulkMailDirectoryRow>>(() => new Map());
  const [recipientSelected, setRecipientSelected] = useState<Map<string, BulkMailDirectoryRow>>(() => new Map());
  const [manualRecipientEmails, setManualRecipientEmails] = useState<string[]>([]);
  const [manualEmailDraft, setManualEmailDraft] = useState("");

  const [subject, setSubject] = useState("GTsetu: Helping You Find Right Global Partners!");
  const [htmlBody, setHtmlBody] = useState("");
  const [initializedPreview, setInitializedPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [signatureTemplate, setSignatureTemplate] = useState<BulkMailSignatureId>("Aditya");

  useEffect(() => {
    setInitializedPreview(false);
  }, [contextSelected]);

  useEffect(() => {
    if (active !== 2) return;
    setHtmlBody((prev) => replaceBulkMailSignature(prev, signatureTemplate));
  }, [active, signatureTemplate]);

  const previousParamsRef = useRef<string>("");

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await trpc.admin.bulkMailDirectory.query({
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: debouncedSearch.trim() || undefined,
      });
      setRows(res.rows as BulkMailDirectoryRow[]);
      setTotalRows(res.meta.total);
    } catch (err) {
      handleTrpcError(err);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize, debouncedSearch]);

  useEffect(() => {
    const paramsString = JSON.stringify({
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      search: debouncedSearch,
    });
    if (paramsString === previousParamsRef.current) {
      return;
    }
    previousParamsRef.current = paramsString;
    void fetchRows();
  }, [fetchRows, pagination.pageIndex, pagination.pageSize, debouncedSearch]);

  useEffect(() => {
    if (pagination.pageIndex !== 0) {
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }
  }, [debouncedSearch]);

  const toggleContext = useCallback((row: BulkMailDirectoryRow) => {
    setContextSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.userId)) next.delete(row.userId);
      else next.set(row.userId, row);
      return next;
    });
  }, []);

  const toggleRecipient = useCallback((row: BulkMailDirectoryRow) => {
    setRecipientSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.userId)) next.delete(row.userId);
      else next.set(row.userId, row);
      return next;
    });
  }, []);

  const togglePageContext = useCallback((pageRows: BulkMailDirectoryRow[], selectAll: boolean) => {
    setContextSelected((prev) => {
      const next = new Map(prev);
      for (const r of pageRows) {
        if (selectAll) next.set(r.userId, r);
        else next.delete(r.userId);
      }
      return next;
    });
  }, []);

  const togglePageRecipient = useCallback((pageRows: BulkMailDirectoryRow[], selectAll: boolean) => {
    setRecipientSelected((prev) => {
      const next = new Map(prev);
      for (const r of pageRows) {
        if (selectAll) next.set(r.userId, r);
        else next.delete(r.userId);
      }
      return next;
    });
  }, []);

  const goNextFromStep1 = () => {
    if (contextSelected.size === 0) {
      notifications.show({
        title: "Select rows",
        message: "Choose at least one user on this step.",
        color: "orange",
      });
      return;
    }
    setActive(1);
  };

  const addManualRecipientEmail = useCallback(() => {
    const raw = manualEmailDraft.trim();
    if (!raw) {
      return;
    }
    if (!isValidEmail(raw)) {
      notifications.show({
        title: "Invalid email",
        message: "Enter a valid email address (e.g. name@example.com).",
        color: "orange",
      });
      return;
    }
    const normalized = raw.trim().toLowerCase();
    const tableEmails = new Set([...recipientSelected.values()].map((r) => r.userEmail.trim().toLowerCase()));
    if (tableEmails.has(normalized) || manualRecipientEmails.some((e) => e.trim().toLowerCase() === normalized)) {
      notifications.show({
        title: "Already added",
        message: "That address is already in the recipient list.",
        color: "orange",
      });
      return;
    }
    setManualRecipientEmails((prev) => [...prev, raw.trim()]);
    setManualEmailDraft("");
  }, [manualEmailDraft, manualRecipientEmails, recipientSelected]);

  const goNextFromStep2 = () => {
    if (recipientSelected.size === 0 && manualRecipientEmails.length === 0) {
      notifications.show({
        title: "Select recipients",
        message: "Pick users from the table and/or add external emails above.",
        color: "orange",
      });
      return;
    }
    if (!initializedPreview) {
      const list = [...contextSelected.values()];
      setHtmlBody(buildDefaultMarketingHtml(list, signatureTemplate));
      setInitializedPreview(true);
    }
    setActive(2);
  };

  const allRecipientEmails = useMemo(() => {
    const fromTable = [...recipientSelected.values()].map((r) => r.userEmail.trim());
    const seen = new Set<string>();
    const out: string[] = [];
    for (const e of [...fromTable, ...manualRecipientEmails.map((x) => x.trim())]) {
      const k = e.toLowerCase();
      if (!k || seen.has(k)) continue;
      seen.add(k);
      out.push(e);
    }
    return out;
  }, [recipientSelected, manualRecipientEmails]);

  const sendEmails = async () => {
    const recipientEmails = allRecipientEmails;
    const sharedCompanyIds: string[] = [];
    const companySeen = new Set<string>();
    for (const r of contextSelected.values()) {
      if (!r.companyId || companySeen.has(r.companyId)) continue;
      companySeen.add(r.companyId);
      sharedCompanyIds.push(r.companyId);
    }
    setSending(true);
    try {
      const result = await trpc.admin.queueBulkMarketingEmails.mutate({
        subject,
        html: htmlBody,
        recipientEmails,
        sharedCompanyIds,
        smtpProfile: signatureTemplate,
      });
      notifications.show({
        title: result.failed > 0 ? "Partially queued" : "Queued for delivery",
        message:
          result.failed > 0
            ? `${result.queued} queued, ${result.failed} failed. Check server logs.`
            : `${result.queued} message(s) queued for sending.`,
        color: result.failed > 0 ? "orange" : "green",
      });
      setContextSelected(new Map());
      setRecipientSelected(new Map());
      setManualRecipientEmails([]);
      setManualEmailDraft("");
      setInitializedPreview(false);
      setHtmlBody("");
      setSubject("GTsetu: Helping You Find Right Global Partners!");
      setActive(0);
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setSending(false);
    }
  };

  const contextSummary = useMemo(() => {
    const byCompany = new Map<string, BulkMailDirectoryRow>();
    for (const r of contextSelected.values()) {
      if (r.companyId && !byCompany.has(r.companyId)) {
        byCompany.set(r.companyId, r);
      }
    }
    return [...byCompany.values()];
  }, [contextSelected]);

  return (
    <Flex direction="column" p="lg" gap="lg" mih="100%">
      <div>
        <Title order={2}>Bulk mail</Title>
        <Text c="dimmed" size="sm" mt={4}>
          Step 1: companies for the template (anonymous discover links only — no names or emails in the message). The
          directory lists client users whose company is VERIFIED by admin (drafts and system-verified
          profiles are excluded). Step 2: recipients. Messages are queued on the mail worker (RabbitMQ when enabled).
        </Text>
        <Anchor component={Link} to="/admin/bulk-mail/history" size="sm" mt={8} display="inline-flex" style={{ alignItems: "center", gap: 6 }}>
          <IconHistory size={16} />
          View send history
        </Anchor>
      </div>

      <Stepper active={active} allowNextStepsSelect={false}>
        <Stepper.Step label="Context" description="Verified companies for template">
          <Paper p="md" mt="md" withBorder>
            <DirectoryTable
              stepLabel="Step 1 — context"
              rows={rows}
              loading={loading}
              totalRows={totalRows}
              pagination={pagination}
              onPaginationChange={setPagination}
              search={search}
              onSearchChange={setSearch}
              selected={contextSelected}
              onToggleRow={toggleContext}
              onTogglePage={togglePageContext}
            />
            <Group justify="flex-end" mt="md">
              <Button onClick={goNextFromStep1}>Next</Button>
            </Group>
          </Paper>
        </Stepper.Step>

        <Stepper.Step label="Recipients" description="Who receives the email">
          <Paper p="md" mt="md" withBorder>
            <Box
              mb="lg"
              p="sm"
              style={{
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: "var(--mantine-radius-md)",
                backgroundColor: "var(--mantine-color-gray-0)",
              }}
            >
              <Text size="sm" fw={600} mb={6}>
                External recipients (not on the platform)
              </Text>
              <Text size="xs" c="dimmed" mb="sm">
                Type an email, press Enter or OK — each address appears as a chip. These are sent together with any
                rows you select in the table below.
              </Text>
              <Group align="flex-end" wrap="nowrap" gap="sm" mb={manualRecipientEmails.length > 0 ? "sm" : 0}>
                <TextInput
                  label="Email address"
                  placeholder="name@example.com"
                  value={manualEmailDraft}
                  onChange={(e) => setManualEmailDraft(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addManualRecipientEmail();
                    }
                  }}
                  style={{ flex: 1 }}
                />
                <Button onClick={addManualRecipientEmail}>Add</Button>
              </Group>
              {manualRecipientEmails.length > 0 ? (
                <Group gap={6} wrap="wrap">
                  {manualRecipientEmails.map((email) => (
                    <Pill
                      key={email}
                      withRemoveButton
                      onRemove={() =>
                        setManualRecipientEmails((prev) => prev.filter((x) => x.toLowerCase() !== email.toLowerCase()))
                      }
                      radius="md"
                      styles={{
                        root: {
                          backgroundColor: "var(--mantine-color-blue-0)",
                          height: 32,
                          paddingInline: 10,
                        },
                        label: { fontSize: 13, fontWeight: 500 },
                      }}
                    >
                      {email}
                    </Pill>
                  ))}
                </Group>
              ) : null}
            </Box>
            <DirectoryTable
              stepLabel="Step 2 — recipients"
              summaryText={`Table: ${recipientSelected.size} selected (across pages)${manualRecipientEmails.length > 0
                  ? ` · External chips: ${manualRecipientEmails.length} · Unique addresses to send: ${allRecipientEmails.length}`
                  : ""
                }`}
              rows={rows}
              loading={loading}
              totalRows={totalRows}
              pagination={pagination}
              onPaginationChange={setPagination}
              search={search}
              onSearchChange={setSearch}
              selected={recipientSelected}
              onToggleRow={toggleRecipient}
              onTogglePage={togglePageRecipient}
            />
            <Group justify="space-between" mt="md">
              <Button variant="default" onClick={() => setActive(0)}>
                Back
              </Button>
              <Button onClick={goNextFromStep2}>Next</Button>
            </Group>
          </Paper>
        </Stepper.Step>

        <Stepper.Step label="Send" description="Review and queue">
          <Paper p="md" mt="md" withBorder>
            <Text size="sm" fw={600} mb="xs">
              Anonymous profile links (from step 1)
            </Text>
            {contextSummary.length === 0 ? (
              <Text size="sm" c="dimmed" mb="md">
                No company rows in step 1 — edit the HTML below as needed.
              </Text>
            ) : (
              <Flex direction="column" gap={6} mb="md">
                {contextSummary.map((r) => {
                  const href = `${getPublicDiscoverOrigin()}/discover/${r.companyId}`;
                  return (
                    <Anchor key={r.companyId ?? r.userId} href={href} size="sm" target="_blank" rel="noopener noreferrer">
                      {href}
                    </Anchor>
                  );
                })}
              </Flex>
            )}

            <Text size="sm" fw={600} mb={6}>
              Recipients ({allRecipientEmails.length})
            </Text>
            <Text size="xs" c="dimmed" mb="sm" style={{ wordBreak: "break-all" }}>
              {allRecipientEmails.join(", ")}
            </Text>

            <Text size="sm" fw={600} mb={6}>
              Send as (signature + SMTP mailbox)
            </Text>
            <Text size="xs" c="dimmed" mb="sm">
              Updates the signature block in the HTML and sends using that person's mailbox from the server
              environment (Jay / Aaryan / Aditya). If you edit the body manually, keep the HTML comment markers around
              the signature so switching here still works.
            </Text>
            <SegmentedControl
              mb="md"
              value={signatureTemplate}
              onChange={(v) => setSignatureTemplate(v as BulkMailSignatureId)}
              data={[
                { label: "Jay", value: "Jay" },
                { label: "Aaryan", value: "Aaryan" },
                { label: "Aditya", value: "Aditya" },
              ]}
            />

            <TextInput
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.currentTarget.value)}
              mb="md"
            />
            <Textarea
              label="HTML body"
              description="Each recipient gets this message. Queued through the server mail pipeline (RabbitMQ when configured)."
              minRows={28}
              value={htmlBody}
              onChange={(e) => setHtmlBody(e.currentTarget.value)}
              mb="md"
              styles={{
                input: {
                  fontFamily: "monospace",
                  fontSize: 13,
                  lineHeight: 1.45,
                  minHeight: "clamp(360px, 58vh, 900px)",
                },
              }}
            />
            <Group justify="space-between">
              <Button variant="default" onClick={() => setActive(1)}>
                Back
              </Button>
              <Button loading={sending} onClick={() => void sendEmails()}>
                Send / queue emails
              </Button>
            </Group>
          </Paper>
        </Stepper.Step>
      </Stepper>
    </Flex>
  );
}
